import { useEffect, useState } from 'react';
import { checkVoucher } from '../../actions/voucherActions';
import { DYNAMIC_FILE_ERRORS, getSlugByRateplan } from '../../helpers/payment';
import { generatePath, matchPath, useHistory, useLocation, useParams } from 'react-router';
import { ROUTE_ACTIVATE_PLAN, ROUTE_ACTIVATE_PLAN_STATUS } from '../../constants/Routes';
import {
	ONBOARDING_VOUCHER_INVALID_RATEPLAN,
	ONBOARDING_VOUCHER_SEND_AGAIN,
	CONTACT_SUBMIT,
} from '../../constants/Copy';
import { ERROR } from '../../constants/ErrorTypes';
import { useDispatch, useSelector } from 'react-redux';
import { PARAM_NAMES } from '../../constants/Checkout';
import { initiateTrackStructEvent } from '../analytics/Snowplow';
import {
	EVT_VOUCHER_CATEGORY,
	EVT_VALIDATE_SUCCESS,
	EVT_VALIDATE_ERROR,
	EVT_ELEMENT_INTERACTION,
	EVT_ELEMENT_TYPE_BUTTON,
	EVT_SUCCESS,
} from '../../constants/SnowplowEvents';
import { getCurrrentPlan } from '../../helpers/state';

const { VOUCHERCODE, VOUCHER_RATE_PLAN_CODE } = PARAM_NAMES;

export function useVoucherCheck(voucher, setIsVoucherValidOnActivate, actionCode) {
	const { transactionMethod, transactionError } = useParams();
	const [isValid, setIsValid] = useState(false);
	const [planId, setPlanId] = useState();
	const [reseller, setReseller] = useState();
	const [isLoading, setIsLoading] = useState(false);
	const [redemptionLeadsTo, setRedemptionLeadsTo] = useState({});
	const dispatch = useDispatch();
	const history = useHistory();
	const location = useLocation();
	const [sendCode, setSendCode] = useState(!!actionCode);
	const [verifyError, setVerifyError] = useState(null);
	const currentPlan = useSelector((state) => getCurrrentPlan(state));
	const currentPlanId = currentPlan?.planDescription.planId;

	useEffect(() => {
		if (sendCode) {
			doHandleVoucherCheck(voucher);
		}

		function doHandleVoucherCheck() {
			(async () => {
				try {
					setIsLoading(true);
					const {
						payload: {
							hasError,
							status,
							planId,
							reseller,
							ratePlanCode,
							redemptionLeadsToPromoLoss,
							redemptionLeadsToSuspensionOfCurrentSubscription,
						},
					} = await dispatch(checkVoucher(voucher, currentPlanId));
					setIsLoading(false);
					setSendCode(false);
					setRedemptionLeadsTo({
						redemptionLeadsToPromoLoss,
						redemptionLeadsToSuspensionOfCurrentSubscription,
					});
					setPlanId(planId);
					setReseller(reseller);

					if (hasError) {
						if (!actionCode)
							initiateTrackStructEvent({
								category: EVT_VOUCHER_CATEGORY,
								action: EVT_VALIDATE_ERROR,
								label: status,
							});
						if (setIsVoucherValidOnActivate) setIsVoucherValidOnActivate(false);
						setVerifyError(status);
					} else {
						if (setIsVoucherValidOnActivate) setIsVoucherValidOnActivate(true);
						setIsValid(true);
						const slug = await getSlugByRateplan(reseller ? VOUCHER_RATE_PLAN_CODE : ratePlanCode);
						if (!actionCode)
							await initiateTrackStructEvent({
								category: EVT_VOUCHER_CATEGORY,
								action: EVT_VALIDATE_SUCCESS,
								label: verifyError ? ONBOARDING_VOUCHER_SEND_AGAIN : CONTACT_SUBMIT,

								rtlElementContext: {
									elementInteraction: EVT_ELEMENT_INTERACTION,
									elementGroup: EVT_VOUCHER_CATEGORY,
									elementType: EVT_ELEMENT_TYPE_BUTTON,
									elementName: EVT_SUCCESS,
									elementLabel: verifyError ? ONBOARDING_VOUCHER_SEND_AGAIN : CONTACT_SUBMIT,
								},
							});
						if (!matchPath(location.pathname, ROUTE_ACTIVATE_PLAN_STATUS)) {
							history.push(
								`${generatePath(ROUTE_ACTIVATE_PLAN, {
									plan: slug,
									transactionMethod,
									transactionError,
								})}?${VOUCHERCODE}=${voucher}`
							);
						}
					}
				} catch (errMessage) {
					setIsLoading(false);
					setSendCode(false);
					if (errMessage === DYNAMIC_FILE_ERRORS.NOT_FOUND) {
						setVerifyError(ONBOARDING_VOUCHER_INVALID_RATEPLAN);
					} else {
						setVerifyError(ERROR);
					}
				}
			})();
		}
	}, [
		voucher,
		location.pathname,
		location.search,
		dispatch,
		history,
		sendCode,
		setIsVoucherValidOnActivate,
		transactionMethod,
		transactionError,
		actionCode,
		currentPlanId,
	]);

	function resetVoucherCheck() {
		setVerifyError(null);
		setSendCode(false);
	}

	return {
		isValid,
		planId,
		reseller,
		isLoading,
		setSendCode,
		verifyError,
		resetVoucherCheck,
		...redemptionLeadsTo,
	};
}
